import { useState } from "react";
import AdminCard from "./AdminCard";

interface Props { onSave: () => void; }

const defaultNavLinks = [
  { id: 1, label: "Home", path: "/", visible: true },
  { id: 2, label: "Properties", path: "/properties", visible: true },
  { id: 3, label: "Products", path: "/social-housing", visible: true },
  { id: 4, label: "About Us", path: "/about", visible: true },
  { id: 5, label: "Contact", path: "/contact", visible: true },
];

export default function AdminNavbar({ onSave }: Props) {
  const [navLinks, setNavLinks] = useState(defaultNavLinks);
  const [brandName, setBrandName] = useState("Thorpe Dellow");
  const [brandTagline, setBrandTagline] = useState("Structured Investments");
  const [phoneUK, setPhoneUK] = useState("+44 20 3097 6185");
  const [phoneIntl, setPhoneIntl] = useState("+1 437 292 2296");
  const [portalUrl, setPortalUrl] = useState("https://portal.thorpedellow.com");
  const [portalLabel, setPortalLabel] = useState("Portal");
  const [ctaLabel, setCtaLabel] = useState("Get in Touch");
  const [ctaLink, setCtaLink] = useState("/contact");
  const [brochureLabel, setBrochureLabel] = useState("Investment Brochure");
  const [brochureVisible, setBrochureVisible] = useState(true);

  const updateLink = (id: number, k: string, v: string | boolean) => {
    setNavLinks((prev) => prev.map((l) => l.id === id ? { ...l, [k]: v } : l));
  };

  const addLink = () => {
    const newId = Math.max(...navLinks.map((l) => l.id)) + 1;
    setNavLinks((prev) => [...prev, { id: newId, label: "New Page", path: "/new-page", visible: true }]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>Navigation</h1>
        <p className="text-slate-500 text-sm mt-1">Manage the navbar brand, menu links, phone numbers, and CTA buttons.</p>
      </div>

      <AdminCard title="Brand Identity">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Brand Name</label>
            <input value={brandName} onChange={(e) => setBrandName(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Tagline (below brand name)</label>
            <input value={brandTagline} onChange={(e) => setBrandTagline(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
        </div>
      </AdminCard>

      <AdminCard
        title="Navigation Links"
        actions={
          <button onClick={addLink} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#C9A84C]/10 text-[#C9A84C] text-xs font-semibold hover:bg-[#C9A84C]/20 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line leading-none" />
            Add Link
          </button>
        }
      >
        <div className="space-y-3">
          {navLinks.map((link) => (
            <div key={link.id} className={`flex items-center gap-3 p-3 rounded-xl border ${link.visible ? "bg-slate-50 border-slate-100" : "bg-slate-50/50 border-slate-100 opacity-60"}`}>
              <div className="w-6 h-6 flex items-center justify-center text-slate-300 cursor-grab flex-shrink-0">
                <i className="ri-drag-move-2-line text-sm leading-none" />
              </div>
              <div className="flex-1 grid grid-cols-2 gap-3">
                <input
                  value={link.label}
                  onChange={(e) => updateLink(link.id, "label", e.target.value)}
                  placeholder="Label"
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]"
                />
                <input
                  value={link.path}
                  onChange={(e) => updateLink(link.id, "path", e.target.value)}
                  placeholder="/path"
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C] font-mono"
                />
              </div>
              <button
                onClick={() => updateLink(link.id, "visible", !link.visible)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer flex-shrink-0 ${link.visible ? "text-green-500 bg-green-50 hover:bg-green-100" : "text-slate-400 bg-slate-100 hover:bg-slate-200"}`}
              >
                <i className={`${link.visible ? "ri-eye-line" : "ri-eye-off-line"} text-sm leading-none`} />
              </button>
              <button
                onClick={() => setNavLinks((prev) => prev.filter((l) => l.id !== link.id))}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-delete-bin-line text-sm leading-none" />
              </button>
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard title="Brochure Link (Mobile Menu)">
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div>
              <p className="text-sm font-semibold text-[#071A2E]">Investment Brochure Link</p>
              <p className="text-xs text-slate-400 mt-0.5">Shown in gold in the mobile hamburger menu</p>
            </div>
            <button
              onClick={() => setBrochureVisible(!brochureVisible)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${brochureVisible ? "bg-green-50 text-green-600 hover:bg-green-100" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}
            >
              <i className={`${brochureVisible ? "ri-eye-line" : "ri-eye-off-line"} leading-none`} />
              {brochureVisible ? "Visible" : "Hidden"}
            </button>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Brochure Link Label</label>
            <input value={brochureLabel} onChange={(e) => setBrochureLabel(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
        </div>
      </AdminCard>

      <AdminCard title="Phone Numbers (Desktop Navbar)">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">UK Phone</label>
            <input value={phoneUK} onChange={(e) => setPhoneUK(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">International Phone</label>
            <input value={phoneIntl} onChange={(e) => setPhoneIntl(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
        </div>
      </AdminCard>

      <AdminCard title="CTA Buttons">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Portal Button Label</label>
            <input value={portalLabel} onChange={(e) => setPortalLabel(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Portal URL</label>
            <input value={portalUrl} onChange={(e) => setPortalUrl(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Primary CTA Label</label>
            <input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Primary CTA Link</label>
            <input value={ctaLink} onChange={(e) => setCtaLink(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
        </div>
      </AdminCard>

      <div className="flex justify-end">
        <button onClick={onSave} className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#071A2E] text-white text-sm font-semibold hover:bg-[#0D2B4A] transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-save-line leading-none" />
          Save Changes
        </button>
      </div>
    </div>
  );
}
